import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
	ticketDeletedEvent,
	ticketReadEvent,
	ticketUpdatedEvent,
	ticketsBulkUpdatedEvent
} from "../../utils/ticketEvents";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		color: "rgb(67, 83, 105)",
		zIndex: 2,
		backgroundColor: "white",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
}));

const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				state[ticketIndex] = ticket;
				if (ticket.unreadMessages > 0) {
					state.unshift(state.splice(ticketIndex, 1)[0]);
				}
			} else {
				state.push(ticket);
			}
		});

		return [...state];
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
		}

		return [...state];
	}

	if (action.type === "CLEAR_ALL_MESSAGES") {
		return state.map(ticket => ({
			...ticket,
			lastMessage: "",
			unreadMessages: 0,
		}));
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = {
				...state[ticketIndex],
				...ticket,
				labels: ticket.labels !== undefined ? ticket.labels : state[ticketIndex].labels,
			};
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = {
				...state[ticketIndex],
				...ticket,
				labels: ticket.labels !== undefined ? ticket.labels : state[ticketIndex].labels,
			};
			state.unshift(state.splice(ticketIndex, 1)[0]);
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		const ticketIndex = state.findIndex(t => t.contactId === contact.id);
		if (ticketIndex !== -1) {
			state[ticketIndex].contact = contact;
		}
		return [...state];
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

const sameId = (left, right) => String(left || "") === String(right || "");

const includesId = (values, value) =>
	Array.isArray(values) && values.some(item => sameId(item, value));

	const TicketsList = (props) => {
		const {
			status,
			searchParam,
			showAll,
			selectedQueueIds,
			selectedLabelIds,
			selectedConnectionIds,
			selectedUserIds,
			chatTypeFilter,
			selectedFunnelId,
			selectedFunnelStageId,
			refreshToken,
			updateCount,
			selectedTicketIds = [],
			selectionMode = false,
			onSelectionChange = () => {},
			style
		} =
			props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const { user } = useContext(AuthContext);
	const queueIdsKey = JSON.stringify(selectedQueueIds || []);
	const labelIdsKey = JSON.stringify(selectedLabelIds || []);
	const connectionIdsKey = JSON.stringify(selectedConnectionIds || []);
	const userIdsKey = JSON.stringify(selectedUserIds || []);
	const activeFunnelId = selectedFunnelId && selectedFunnelStageId ? selectedFunnelId : "";
	const activeFunnelStageId = activeFunnelId ? selectedFunnelStageId : "";
	const [lastSelectedTicketId, setLastSelectedTicketId] = useState(null);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [
		status,
		searchParam,
		dispatch,
		showAll,
		queueIdsKey,
		labelIdsKey,
		connectionIdsKey,
		userIdsKey,
		chatTypeFilter,
		activeFunnelId,
		activeFunnelStageId,
		refreshToken
	]);

	useEffect(() => {
		if (!selectedTicketIds.length) {
			setLastSelectedTicketId(null);
		}
	}, [selectedTicketIds.length]);

	const { tickets, hasMore, loading } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		queueIds: queueIdsKey,
		labelIds: labelIdsKey,
		connectionIds: connectionIdsKey,
		userIds: userIdsKey,
		contactType: chatTypeFilter,
		funnelId: activeFunnelId,
		funnelStageId: activeFunnelStageId,
		refreshToken,
		withUnreadMessages: chatTypeFilter === "unread" ? "true" : undefined,
	});

	useEffect(() => {
		if (!status && !searchParam) return;
		dispatch({
			type: "LOAD_TICKETS",
			payload: tickets,
		});
	}, [tickets]);

	useEffect(() => {
		const socket = openSocket();

		const selectedQueues = selectedQueueIds || [];
		const selectedConnections = selectedConnectionIds || [];
		const selectedUsers = selectedUserIds || [];
		const selectedLabels = selectedLabelIds || [];

		const shouldUpdateTicket = ticket => {
			if (searchParam) return null;
			if (activeFunnelId && activeFunnelStageId) return null;
			if (status && ticket.status !== status) return false;
			if (!showAll && ticket.status !== "pending" && !sameId(ticket.userId, user?.id)) {
				return false;
			}
			if (ticket.queueId && !includesId(selectedQueues, ticket.queueId)) {
				return false;
			}
			if (selectedConnections.length &&
				!includesId(selectedConnections, ticket.connectionId)) {
				return false;
			}
			if (selectedUsers.length && !includesId(selectedUsers, ticket.userId)) {
				return false;
			}
			if (selectedLabels.length &&
				!ticket.labels?.some(label => includesId(selectedLabels, label.id))) {
				return false;
			}
			if (chatTypeFilter === "groups" && !ticket.isGroup) {
				return false;
			}
			if (chatTypeFilter === "contacts" && ticket.isGroup) {
				return false;
			}
			if (chatTypeFilter === "unread" && Number(ticket.unreadMessages || 0) <= 0) {
				return false;
			}
			if (chatTypeFilter === "ignored" && !ticket.isIgnored) {
				return false;
			}
			if (chatTypeFilter !== "ignored" && ticket.isIgnored) {
				return false;
			}
			return true;
		};

		socket.on("connect", () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
		});

		socket.on("ticket", data => {
			const ticketBelongsToList =
				data.ticket ? shouldUpdateTicket(data.ticket) : null;

			if (data.action === "updateUnread") {
				const unreadTicketId = data.ticketId || data.ticket?.id;
				if (chatTypeFilter === "unread" && unreadTicketId) {
					dispatch({ type: "DELETE_TICKET", payload: unreadTicketId });
				} else {
					dispatch({
						type: "RESET_UNREAD",
						payload: unreadTicketId,
					});
				}
			}

			if (data.action === "update" && ticketBelongsToList === true) {
				dispatch({
					type: "UPDATE_TICKET",
					payload: data.ticket,
				});
			}

			if (data.action === "update" && ticketBelongsToList === false) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
			}
		});

		socket.on("appMessage", data => {
			if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
				dispatch({
					type: "UPDATE_TICKET_UNREAD_MESSAGES",
					payload: {
						...data.ticket,
						lastMessageAt: data.ticket.lastMessageAt || data.message?.createdAt
					},
				});
			}
		});

		socket.on("companyChatsCleared", () => {
			dispatch({ type: "CLEAR_ALL_MESSAGES" });
		});

		socket.on("contact", data => {
			if (data.action === "update") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
				});
			}
		});

		const handleTicketUpdated = event => {
			const ticket = event.detail?.ticket;
			if (!ticket) return;

			const ticketBelongsToList = shouldUpdateTicket(ticket);
			if (ticketBelongsToList === true) {
				dispatch({
					type: "UPDATE_TICKET",
					payload: ticket,
				});
			}
			if (ticketBelongsToList === false) {
				dispatch({ type: "DELETE_TICKET", payload: ticket.id });
			}
		};

		const handleTicketRead = event => {
			const ticket = event.detail?.ticket;
			const ticketId = event.detail?.ticketId || ticket?.id;
			if (!ticketId) return;

			if (ticket) {
				const ticketBelongsToList = shouldUpdateTicket(ticket);
				if (ticketBelongsToList === true) {
					dispatch({
						type: "UPDATE_TICKET",
						payload: ticket,
					});
				}
				if (ticketBelongsToList === false) {
					dispatch({ type: "DELETE_TICKET", payload: ticket.id });
				}
				return;
			}

			dispatch({
				type: "RESET_UNREAD",
				payload: ticketId,
			});
		};

		const handleTicketsBulkUpdated = event => {
			const updatedTickets = Array.isArray(event.detail?.tickets)
				? event.detail.tickets
				: [];

			updatedTickets.forEach(ticket => {
				const ticketBelongsToList = shouldUpdateTicket(ticket);
				if (ticketBelongsToList === true) {
					dispatch({
						type: "UPDATE_TICKET",
						payload: ticket,
					});
				}
				if (ticketBelongsToList === false) {
					dispatch({ type: "DELETE_TICKET", payload: ticket.id });
				}
			});
		};

		const handleTicketDeleted = event => {
			const ticketId = event.detail?.ticketId;
			if (ticketId) {
				dispatch({ type: "DELETE_TICKET", payload: ticketId });
			}
		};

		window.addEventListener(ticketUpdatedEvent, handleTicketUpdated);
		window.addEventListener(ticketReadEvent, handleTicketRead);
		window.addEventListener(ticketsBulkUpdatedEvent, handleTicketsBulkUpdated);
		window.addEventListener(ticketDeletedEvent, handleTicketDeleted);

		return () => {
			window.removeEventListener(ticketUpdatedEvent, handleTicketUpdated);
			window.removeEventListener(ticketReadEvent, handleTicketRead);
			window.removeEventListener(ticketsBulkUpdatedEvent, handleTicketsBulkUpdated);
			window.removeEventListener(ticketDeletedEvent, handleTicketDeleted);
			socket.disconnect();
		};
	}, [
		status,
		searchParam,
		showAll,
		user,
		selectedQueueIds,
		selectedLabelIds,
		selectedConnectionIds,
		selectedUserIds,
		chatTypeFilter,
		activeFunnelId,
		activeFunnelStageId
	]);

	useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			e.currentTarget.scrollTop = scrollTop - 100;
			loadMore();
		}
	};

	const handleToggleSelection = (ticket, event, ticketIndex) => {
		const ticketId = Number(ticket.id);
		const currentIds = selectedTicketIds.map(Number);
		const visibleIds = ticketsList.map(item => Number(item.id));
		const visibleIdsSet = new Set(visibleIds);

		if (event?.shiftKey && lastSelectedTicketId) {
			const startIndex = ticketsList.findIndex(item => Number(item.id) === Number(lastSelectedTicketId));
			const endIndex = ticketIndex;

			if (startIndex !== -1 && endIndex !== -1) {
				const [start, end] = [startIndex, endIndex].sort((a, b) => a - b);
				const rangeIds = ticketsList.slice(start, end + 1).map(item => Number(item.id));
				const selectedOutsideVisibleList = currentIds.filter(id => !visibleIdsSet.has(id));
				onSelectionChange(Array.from(new Set([...selectedOutsideVisibleList, ...rangeIds])));
				return;
			}
		}

		onSelectionChange(
			currentIds.includes(ticketId)
				? currentIds.filter(item => item !== ticketId)
				: [...currentIds, ticketId]
		);
		setLastSelectedTicketId(ticketId);
	};

	return (
    <Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{ticketsList.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<>
							{ticketsList.map((ticket, index) => (
								<TicketListItem
									ticket={ticket}
									key={ticket.id}
									selectionMode={selectionMode}
									selected={selectedTicketIds.map(Number).includes(Number(ticket.id))}
									onToggleSelection={(selectedTicket, event) =>
										handleToggleSelection(selectedTicket, event, index)
									}
								/>
							))}
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;

